import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, { setToken, getToken, USER_KEY } from '../api/client';

const AuthContext = createContext(null);

// Ported from the app's AuthContext — same API surface so screens behave
// identically; localStorage stands in for AsyncStorage.
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setTok] = useState(null);
  const [restoring, setRestoring] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState(null);

  // Restore a saved session on load so the user stays logged in.
  useEffect(() => {
    try {
      const t = getToken();
      const u = localStorage.getItem(USER_KEY);
      if (t) setTok(t);
      if (u) setUser(JSON.parse(u));
    } catch (e) {
      /* ignore */
    } finally {
      setRestoring(false);
    }
  }, []);

  // Persist token + user whenever they change (after the initial restore).
  useEffect(() => {
    if (restoring) return;
    setToken(token);
    try {
      if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
      else localStorage.removeItem(USER_KEY);
    } catch (e) {
      /* ignore */
    }
  }, [token, user, restoring]);

  const logout = useCallback(() => {
    setToken(null);
    setTok(null);
    setUser(null);
    try { localStorage.removeItem(USER_KEY); } catch (e) { /* ignore */ }
  }, []);

  // A 401 anywhere in the app drops us back to the login screen.
  useEffect(() => {
    const onUnauthorized = () => { setTok(null); setUser(null); };
    window.addEventListener('nry:unauthorized', onUnauthorized);
    return () => window.removeEventListener('nry:unauthorized', onUnauthorized);
  }, []);

  const requestOtp = useCallback(async (mobile, role = 'customer', referralCode) => {
    return api.post('/auth/request-otp', { mobile, role, referralCode });
  }, []);

  const verifyOtp = useCallback(async (mobile, otp) => {
    const data = await api.post('/auth/verify-otp', { mobile, otp });
    setToken(data.token);
    setTok(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  // Re-fetch the current user (e.g. to pick up admin KYC approval).
  const refreshUser = useCallback(async () => {
    try {
      const data = await api.get('/users/me');
      if (data?.user) setUser(data.user);
      return data?.user;
    } catch (e) {
      return null;
    }
  }, []);

  // Staff may use the app only once KYC is approved.
  const canAccess =
    !user || user.role !== 'staff' || user.canAccess === true || user?.kyc?.status === 'approved';

  return (
    <AuthContext.Provider
      value={{
        user, token, restoring, selectedLocation, setSelectedLocation,
        requestOtp, verifyOtp, refreshUser, logout, setUser,
        isAuthed: !!token, canAccess,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
